
import { toast } from 'react-hot-toast';
import { CampaignFormData, WorkflowStep } from '../types';
import { generateConsolidatedReport, formatConsolidatedReport } from './reportGenerator';
import { generatePDF } from './pdf';
import { updateCampaignData } from './firebase';

export const handleGenerateConsolidatedReport = async (
  formData: CampaignFormData,
  steps: WorkflowStep[],
  campaignId: string | null,
  currentStep: number,
  setConsolidatedReport: (report: string) => void,
  setSelectedReport: (report: { title: string; content: string }) => void,
  setIsGeneratingConsolidated: (generating: boolean) => void
) => {
  setIsGeneratingConsolidated(true);
  try {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    if (!apiKey) {
      throw new Error('Gemini API key is not configured');
    }

    const report = await generateConsolidatedReport(formData, steps, apiKey);
    const formattedReport = formatConsolidatedReport(report);

    setConsolidatedReport(formattedReport);
    setSelectedReport({
      title: report.title,
      content: formattedReport,
    });

    if (campaignId) {
      await updateCampaignData(campaignId, {
        analysisState: {
          steps,
          currentStep,
          isComplete: true,
          stepResults: steps.reduce((acc, step) => ({
            ...acc,
            [step.id]: step.result
          }), {}),
          consolidatedReport: formattedReport
        }
      });
    }
  } catch (error) {
    console.error('Error generating consolidated report:', error);
    toast.error('Failed to generate consolidated report');
  } finally {
    setIsGeneratingConsolidated(false);
  }
};

export const downloadAllReports = async (
  formData: CampaignFormData,
  steps: WorkflowStep[],
  consolidatedReport: string | null
) => {
  try {
    const container = document.createElement('div');
    container.className = 'reports-container';

    const titlePage = document.createElement('div');
    titlePage.innerHTML = `
      <h1 style="font-size: 24px; margin-bottom: 20px;">${formData.core_issue_details.campaign_name}</h1>
      <p style="margin-bottom: 10px;"><strong>Political Issue:</strong> ${formData.core_issue_details.political_issue}</p>
      <p style="margin-bottom: 10px;"><strong>Target Entity:</strong> ${formData.core_issue_details.target_entity}</p>
      <p style="margin-bottom: 10px;"><strong>Winning Aspiration:</strong> ${formData.campaign_outcome.winning_aspiration}</p>
      <p style="margin-bottom: 20px;"><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
    `;
    container.appendChild(titlePage);

    steps.forEach(step => {
      if (step.result) {
        const reportDiv = document.createElement('div');
        reportDiv.innerHTML = `
          <h2 style="font-size: 20px; margin-top: 30px; margin-bottom: 15px;">${step.title}</h2>
          <div style="white-space: pre-wrap;">${step.result}</div>
        `;
        container.appendChild(reportDiv);
      }
    });

    if (consolidatedReport) {
      const consolidatedDiv = document.createElement('div');
      consolidatedDiv.innerHTML = `
        <h2 style="font-size: 20px; margin-top: 30px; margin-bottom: 15px;">Consolidated Political Campaign Analysis Report</h2>
        <div style="white-space: pre-wrap;">${consolidatedReport}</div>
      `;
      container.appendChild(consolidatedDiv);
    }

    const fileName = `${formData.core_issue_details.campaign_name.toLowerCase().replace(/\s+/g, '-')}-political-campaign-analysis.pdf`;
    await generatePDF(container, fileName);
    toast.success('All reports downloaded successfully');
  } catch (error) {
    console.error('Error downloading reports:', error);
    toast.error('Failed to download reports');
  }
};
